import { Button, Flex, Grid, Group, NumberInput, SegmentedControl, Text, Textarea, TextInput } from '@mantine/core';
import {
  IconBrandWhatsapp,
  IconCopy,
  IconDeviceFloppy,
  IconInfoCircle,
  IconMail,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import Regex from '../../constants/Regex';
import getMergeTags from '../../constants/getMergeTags';
import useFetch from '../../helpers/useFetch';
import { validateHandlebarsValue } from '../../helpers/validateHandlebars';
import { validateUrlProtocol } from '../../helpers/validateURL';
import { isValidCard } from '../../helpers/whatsappCarouselValidation';
import AssetPickerModal from '../Assets/AssetPickerModal';
import DailyLimitSettings from '../DailyLimitSettings';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import MergeTagsCollapsible from '../MergeTagsCollapsible';
import ConfirmationModal from '../Modals/ConfirmationModal';
import SendTestMessageModal from '../Modals/SendTestMessageModal';
import ProductFeedSelector from '../ProductFeeds/ProductFeedSelector';
import Spinner from '../Spinner';
import WhatsappCarouselCardEditor from './WhatsappCarouselCardEditor';
import WhatsappCarouselCardList from './WhatsappCarouselCardList';

// Meta lints every template text component for a variable-to-words ratio ("Params Words Ratio
// Exceeds Limit") - two variables need a full sentence of static text around them to pass review.
const RECOMMENDER_CARD_BODY_PREFILL =
  'Get {{product.name}} today for just {{formatPrice product "price"}} - limited stock, tap below to order!';
const RECOMMENDER_CAROUSEL_BODY_PREFILL = 'Hi {{firstName}}! We picked these just for you. Tap a card to shop!';
// Module-level so the object identity is stable across renders - the card editor resets its
// local state when the `card` prop identity changes, and an inline literal would wipe
// in-progress typing on any parent re-render (e.g. a late coupons/surveys fetch resolving).
const EMPTY_CARD = { imageUrl: '', btnUrl: '' };
// Meta rejects templates whose URL-button label exceeds 25 characters.
const MAX_BUTTON_TEXT_LENGTH = 25;
// Meta's subcode 2388299: a template text may not start or end with a variable. Shared by the
// body and card-body checks so the two can't drift.
const EDGE_MERGE_TAG_REGEX = /(^\s*{{\s*\w.*?\s*}})|({{\s*\w.*?\s*}}\s*$)/;
// Mantine's NumberInput emits the raw STRING for partial entries ('', '03'); normalize so an
// in-range value is never submitted as a string. Exported for direct unit testing.
export const normalizeCardCount = (value) => (typeof value === 'number' ? value : Number(value) || null);

const WhatsappMessageForm = ({
  whatsappMessage: passedWhatsappMessage,
  saveWhatsappMessage,
  deleteWhatsappMessage,
  showUserFlow,
  onBack,
}) => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { domain } = useSelector((state) => state.domain);
  const [dynamicFieldsOpen, setDynamicFieldsOpen] = useState(false);

  const [customFields, setCustomFields] = useState();
  const [whatsappMessage, setWhatsappMessage] = useState(() => {
    if (!passedWhatsappMessage) return passedWhatsappMessage;
    const init = { ...passedWhatsappMessage };
    if (init.onDailyLimitReached === 'send') init.onDailyLimitReached = 'skip';
    // Persist the static-mode default for multimedia/carousel templates that arrive without one
    // (e.g. a server-created template or "Make a copy" of a pre-recommender template). Otherwise
    // the selector visually shows "Static" but `mode` stays undefined in state until the user clicks.
    if (['multimedia', 'carousel'].includes(init.type) && !init.mode) init.mode = 'static';
    // Attach the drag-and-drop identity up front so it's stable for the whole session (see
    // nextCardSortId below); it's stripped again in buildPayload before saving.
    if (Array.isArray(init.cards)) {
      init.cards = init.cards.map((card, index) => ({ ...card, _sortId: `card-${index}` }));
    }
    return init;
  });
  // Monotonic counter for card `_sortId`s. Seeded past the ids assigned above so a newly added
  // card never collides with a pre-existing one.
  const cardSortIdRef = useRef(passedWhatsappMessage?.cards?.length || 0);
  const nextCardSortId = () => `card-${cardSortIdRef.current++}`;
  // Index of the carousel card open in the editor modal; equals cards.length when adding a new
  // card (the card is only appended on save, so cancelling never leaves an empty invalid card).
  const [editingCardIndex, setEditingCardIndex] = useState(null);
  const [cardBodyTagsOpen, setCardBodyTagsOpen] = useState(false);
  const [error, setError] = useState();
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [coupons, setCoupons] = useState();
  const [surveys, setSurveys] = useState([]);
  const [testWhatsappMessageOpen, setTestWhatsappMessageOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [isValidField, setIsValidField] = useState({
    header: true,
    footer: true,
  });

  const maxTemplateNameLength = 500;
  const maxHeaderOrFooterMessageLength = 60;
  const maxMessageLength = 1024;
  const statuses = ['APPROVED', 'REJECTED', 'PAUSED'];

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/customFields`)
      .then(({ customFields }) => setCustomFields(customFields))
      .catch(setError);

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/coupons`)
      .then(({ coupons }) => setCoupons(coupons))
      .catch(setError);

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/surveys`)
      .then(({ surveys }) => setSurveys(surveys))
      .catch(setError);
  }, [domain.accountId, domain.id, fetch]);

  const handleChange = (key, value) => {
    setWhatsappMessage({ ...whatsappMessage, [key]: value });
  };

  const handleFieldChange = (key, value) => {
    setIsValidField({ ...isValidField, [key]: value.length <= maxHeaderOrFooterMessageLength });
    handleChange(key, value);
  };

  const isInvalidName = (name) => {
    return !/^[a-z\d_]+$/.test(name);
  };

  const hasTags = (text) => {
    return /{{.*}}/.test(text);
  };

  const send = (sendTo, impersonateProfileQuery, triggerEventAction, cb) => {
    setError(undefined);
    setSending(true);
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/whatsappMessages/testWhatsappMessage`, {
      method: 'POST',
      body: JSON.stringify({
        whatsappMessageId: whatsappMessage.id,
        sendTo,
        impersonateProfileQuery,
        triggerEventAction,
      }),
    })
      .then(cb)
      .catch(setError)
      .finally(() => setSending(false));
    return false;
  };

  const noEditing = whatsappMessage.id ? !statuses.includes(whatsappMessage.status) : false;

  if (whatsappMessage === undefined || !customFields) {
    return <Spinner />;
  }

  if (whatsappMessage === null) {
    return <ErrorMessage message="Whatsapp message does not exist!" />;
  }

  const bodyHandlebarsError = validateHandlebarsValue(whatsappMessage.body);
  const headerHandlebarsError = validateHandlebarsValue(whatsappMessage.header);
  const btnUrlHandlebarsError = validateHandlebarsValue(whatsappMessage.btnUrl);
  // Derived rather than held in state: several code paths write btnUrl outside the input's
  // onChange (the type switch clears it, the recommender mode switch pre-fills it), and a stored
  // validity flag goes stale on every one of them - blocking Save on a perfectly valid pre-fill.
  const isValidUrl =
    !whatsappMessage.btnUrl ||
    Regex.mergeTagsRegex.test(whatsappMessage.btnUrl) ||
    validateUrlProtocol(whatsappMessage.btnUrl);

  // Meta rejects template bodies whose mapped numbered placeholder is at the very start or end of
  // the text (error subcode 2388299 "Leading or Trailing Params Not Allowed"). Detect on the
  // Releva-side merge tag so the user sees the issue before saving. Match against the merge-tag
  // shape rather than raw `{{`/`}}` so we don't false-positive on literal braces in copy.
  const bodyEdgeMergeTagError =
    whatsappMessage.body && EDGE_MERGE_TAG_REGEX.test(whatsappMessage.body)
      ? 'Body cannot start or end with a merge tag — Meta rejects this for all template bodies. Add text before/after the tag.'
      : null;

  const isRecommender =
    ['multimedia', 'carousel'].includes(whatsappMessage.type) && whatsappMessage.mode === 'recommender';
  const isCarousel = whatsappMessage.type === 'carousel';

  // {{product.*}} only resolves per-recipient in recommender mode. Outside recommender there's no
  // product context, so the tag would be sent literally and arrive as broken text. Guard body and
  // the multimedia URL fields equally — the equality-based cleanup on mode revert only catches the
  // exact placeholders we pre-fill, not user-typed or composed values.
  // Matches both the property form `{{product.name}}` and the helper-argument form
  // `{{formatPrice product "price"}}` that our own recommender pre-fills use. `\bproduct\b` keeps
  // unrelated tags like `{{productCode}}` from matching.
  const hasProductTag = (value) => !!value && /{{[^}]*\bproduct\b/.test(value);
  // Check the carousel case first: in a carousel "switch to recommender mode" is never the right
  // advice, because recommender carousels reject product tags in the top-level body too.
  const bodyProductTagError = !hasProductTag(whatsappMessage.body)
    ? null
    : isCarousel
      ? 'Carousel message bodies cannot contain {{product.*}} tags - with multiple cards the reference is ambiguous. Use them in the card body instead.'
      : !isRecommender
        ? 'Body contains {{product.*}} tags but recommender mode is not enabled. Remove them or switch to recommender mode.'
        : null;
  const btnUrlProductTagError =
    !isRecommender && hasProductTag(whatsappMessage.btnUrl)
      ? 'Button URL contains {{product.*}} tags but recommender mode is not enabled.'
      : null;
  const imageUrlProductTagError =
    !isRecommender && hasProductTag(whatsappMessage.imageUrl)
      ? 'Image URL contains {{product.*}} tags but recommender mode is not enabled.'
      : null;

  // Carousel card body: one shared template applied to every card, max 160 chars on Meta's side.
  const maxCardBodyLength = 160;
  const cardBodyHandlebarsError = validateHandlebarsValue(whatsappMessage.cardBody);
  const cardBodyEdgeMergeTagError =
    whatsappMessage.cardBody && EDGE_MERGE_TAG_REGEX.test(whatsappMessage.cardBody)
      ? 'Card body cannot start or end with a merge tag - Meta rejects this for template bodies. Add text before/after the tag.'
      : null;
  const cardBodyProductTagError =
    !isRecommender && hasProductTag(whatsappMessage.cardBody)
      ? 'Card body contains {{product.*}} tags but recommender mode is not enabled.'
      : null;
  const cardBodyLengthError =
    (whatsappMessage.cardBody?.length || 0) > maxCardBodyLength
      ? `Card body must be at most ${maxCardBodyLength} characters.`
      : null;
  const cards = whatsappMessage.cards || [];
  const areCardsValid = cards.length >= 2 && cards.length <= 10 && cards.every(isValidCard);

  // Required-field errors for the recommender-carousel inputs. Held as values shared by the input's
  // `error` prop and the Save gate below so the two can't disagree - a required field that blocks
  // Save without rendering a message is a dead end (the user sees a greyed-out button and no cause).
  const carouselNumCardsError =
    isCarousel && isRecommender
      ? !whatsappMessage.numCards
        ? 'Number of cards is required.'
        : whatsappMessage.numCards < 2 || whatsappMessage.numCards > 10
          ? 'Number of cards must be between 2 and 10.'
          : null
      : null;
  const carouselImageUrlError =
    isCarousel && isRecommender && !whatsappMessage.imageUrl
      ? 'Image URL is required - Meta needs a sample image to approve the template.'
      : null;

  // Strip everything the current type/mode doesn't render. Stale values reach state from a
  // mid-session type/mode switch or from a template created outside this form, and submitting
  // them would build Meta components the user never saw - a FOOTER on a carousel (rejected
  // outright), a numCards that contradicts the cards array, or literal {{product.*}} text in a
  // static template. Applied at submit time so it also covers state we never had a chance to
  // clean up on a selector change.
  const buildPayload = () => {
    const payload = { ...whatsappMessage, cards: cards.map(({ imageUrl, btnUrl }) => ({ imageUrl, btnUrl })) };
    if (isCarousel) {
      // Meta carousels have neither header nor footer components.
      payload.header = '';
      payload.footer = '';
      if (isRecommender) {
        payload.cards = [];
      } else {
        payload.numCards = null;
        payload.btnUrl = '';
        payload.imageUrl = '';
        payload.productFeedId = null;
      }
    } else {
      // Carousel-only fields are dead data on text and multimedia templates.
      payload.cards = [];
      payload.numCards = null;
      payload.cardBody = '';
    }
    return payload;
  };

  const save = (e) => {
    e.preventDefault();
    setSaving(true);
    saveWhatsappMessage(buildPayload()).finally(() => setSaving(false));
  };

  const isValid =
    whatsappMessage.name &&
    whatsappMessage.name?.length <= maxTemplateNameLength &&
    whatsappMessage.body &&
    whatsappMessage.body?.length <= maxMessageLength &&
    isValidField.header &&
    isValidField.footer &&
    (whatsappMessage.type !== 'multimedia' ||
      ((!whatsappMessage.btnUrl || isValidUrl) &&
        whatsappMessage.imageUrl &&
        !!whatsappMessage.btnText === !!whatsappMessage.btnUrl &&
        !btnUrlHandlebarsError &&
        !btnUrlProductTagError &&
        !imageUrlProductTagError)) &&
    (!isCarousel ||
      (!!whatsappMessage.btnText &&
        whatsappMessage.btnText.length <= MAX_BUTTON_TEXT_LENGTH &&
        !cardBodyHandlebarsError &&
        !cardBodyEdgeMergeTagError &&
        !cardBodyProductTagError &&
        !cardBodyLengthError &&
        (isRecommender
          ? !carouselNumCardsError &&
            !!whatsappMessage.btnUrl &&
            isValidUrl &&
            !btnUrlHandlebarsError &&
            !carouselImageUrlError
          : areCardsValid))) &&
    (!isRecommender || !!whatsappMessage.productFeedId) &&
    (whatsappMessage.type !== 'text' || !headerHandlebarsError) &&
    !bodyHandlebarsError &&
    !bodyEdgeMergeTagError &&
    !bodyProductTagError;

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  let mediaConfig;
  if (whatsappMessage.type === 'multimedia') {
    mediaConfig = (
      <>
        <Grid mb="sm">
          <Grid.Col span={2}>
            <Text size="sm" fw={500}>
              Mode
            </Text>
          </Grid.Col>
          <Grid.Col span={10}>
            <SegmentedControl
              disabled={!!whatsappMessage.id}
              value={whatsappMessage.mode || 'static'}
              onChange={(mode) => {
                const change = { mode };
                if (mode === 'recommender') {
                  if (!whatsappMessage.btnText) change.btnText = 'Shop Now';
                  if (!whatsappMessage.btnUrl) change.btnUrl = '{{product.url}}';
                  // imageUrl pre-fill is a UX hint: it signals to the user that the runtime image
                  // comes from the product feed. The backend's resolveRecommenderSampleImage detects
                  // this placeholder at save time and substitutes one real product image from the
                  // selected feed as the Meta-approval sample (per-recipient sends still use the
                  // live product image).
                  if (!whatsappMessage.imageUrl) change.imageUrl = '{{product.imageUrl}}';
                  if (!whatsappMessage.body) {
                    // Meta rejects template bodies that start or end with a {{variable}}. Keep this
                    // pre-fill on a single line with clear static text on both edges to make the rule
                    // impossible to violate accidentally.
                    change.body =
                      'Hi {{firstName}}! Check out {{product.name}} for just {{formatPrice product "price"}} — limited time offer, shop now!';
                  }
                } else {
                  change.productFeedId = null;
                  // Drop any product-tag placeholders we pre-filled on entry so they don't get
                  // submitted as literal `{{product.url}}` / `{{product.imageUrl}}` text in static
                  // mode (no per-recipient product context to resolve against).
                  if (whatsappMessage.btnUrl === '{{product.url}}') change.btnUrl = '';
                  if (whatsappMessage.imageUrl === '{{product.imageUrl}}') change.imageUrl = '';
                }
                setWhatsappMessage({ ...whatsappMessage, ...change });
              }}
              data={[
                { label: 'Static', value: 'static' },
                { label: 'Recommender', value: 'recommender' },
              ]}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Recommender mode picks one product per recipient from the selected product feed. The product&apos;s image
              replaces the header at send time; merge tags like {`{{product.name}}`}, {`{{product.url}}`}, and{' '}
              {`{{product.price}}`} resolve per recipient.
            </Text>
          </Grid.Col>
        </Grid>

        {isRecommender && (
          <Grid mb="sm">
            <Grid.Col span={2}>
              <Text size="sm" fw={500}>
                Product Feed*
              </Text>
            </Grid.Col>
            <Grid.Col span={10}>
              <ProductFeedSelector
                accountId={domain.accountId}
                domainId={domain.id}
                productFeedId={whatsappMessage.productFeedId}
                onSelect={(productFeedId) => setWhatsappMessage({ ...whatsappMessage, productFeedId })}
                scope="marketingChannels"
                forType="whatsapp message"
              />
            </Grid.Col>
          </Grid>
        )}

        <Grid mb="sm">
          <Grid.Col span={2}>
            <Text size="sm" fw={500}>
              Button Text
            </Text>
          </Grid.Col>
          <Grid.Col span={10}>
            <TextInput
              name="btnText"
              value={whatsappMessage.btnText || ''}
              placeholder="e.g. Check This Out!"
              error={
                whatsappMessage.btnUrl && !whatsappMessage.btnText
                  ? 'Button Text is required when Button URL is set.'
                  : null
              }
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              disabled={noEditing}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="sm">
          <Grid.Col span={2}>
            <Text size="sm" fw={500}>
              Button URL
            </Text>
          </Grid.Col>
          <Grid.Col span={10}>
            <TextInput
              name="btnUrl"
              value={whatsappMessage.btnUrl || ''}
              placeholder={`e.g. ${domain.url}`}
              error={
                btnUrlHandlebarsError ||
                btnUrlProductTagError ||
                (whatsappMessage.btnUrl && !isValidUrl
                  ? 'Invalid URL. Please check the address for typing errors or missing protocol.'
                  : whatsappMessage.btnText && !whatsappMessage.btnUrl
                    ? 'Button URL is required when Button Text is set.'
                    : null)
              }
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              disabled={noEditing}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="sm">
          <Grid.Col span={2}>
            <Text size="sm" fw={500}>
              Image URL*
            </Text>
          </Grid.Col>
          <Grid.Col span={10}>
            <Flex gap="xs">
              <TextInput
                style={{ flex: 1 }}
                name="imageUrl"
                value={whatsappMessage.imageUrl || ''}
                placeholder="e.g. https://google.com/panda.jpg"
                error={
                  imageUrlProductTagError ||
                  ((passedWhatsappMessage?.imageUrl || whatsappMessage.btnText || whatsappMessage.btnUrl) &&
                  !whatsappMessage.imageUrl
                    ? 'Image URL is required for media-based messages.'
                    : null)
                }
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                disabled={noEditing}
              />
              <Button onClick={() => setImagePickerOpen(true)}>Browse</Button>
            </Flex>
            <AssetPickerModal
              key="whatsapp-asset"
              show={imagePickerOpen}
              close={() => setImagePickerOpen(false)}
              confirm={(asset, _, cb) => {
                setWhatsappMessage({ ...whatsappMessage, imageUrl: asset.url });
                cb();
              }}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Supported image formats: JPEG, JPG, PNG. Recommended image thumbnail size is 400x400.
              {isRecommender
                ? ' In recommender mode this image is only used as the sample for Meta template approval — actual sends use each recipient’s product image. By default the platform picks a real product image from the selected feed automatically; click Browse only if you want to override that sample.'
                : ''}
            </Text>
          </Grid.Col>
        </Grid>
      </>
    );
  }

  // Cards are only appended on editor save (editingCardIndex === cards.length means "new card"),
  // so cancelling the editor never leaves an empty invalid card behind.
  const saveCard = (card) => {
    const nextCards = [...cards];
    nextCards[editingCardIndex] = {
      imageUrl: card.imageUrl,
      btnUrl: card.btnUrl,
      // Keep the existing drag id when editing; mint a new one for an appended card.
      _sortId: cards[editingCardIndex]?._sortId ?? nextCardSortId(),
    };
    setWhatsappMessage({ ...whatsappMessage, cards: nextCards });
    setEditingCardIndex(null);
  };

  const deleteCard = (index) => {
    setWhatsappMessage({ ...whatsappMessage, cards: cards.filter((_, cardIndex) => cardIndex !== index) });
  };

  // Meta freezes the carousel card count at template creation, so a saved template can edit card
  // contents but never add, remove, or reorder cards.
  const cardCountLocked = !!whatsappMessage.id || noEditing;

  let carouselConfig;
  if (isCarousel) {
    carouselConfig = (
      <>
        <Grid mb="sm">
          <Grid.Col span={2}>
            <Text size="sm" fw={500}>
              Mode
            </Text>
          </Grid.Col>
          <Grid.Col span={10}>
            <SegmentedControl
              disabled={!!whatsappMessage.id}
              value={whatsappMessage.mode || 'static'}
              onChange={(mode) => {
                const change = { mode };
                if (mode === 'recommender') {
                  if (!whatsappMessage.btnText) change.btnText = 'Shop Now';
                  if (!whatsappMessage.btnUrl) change.btnUrl = '{{product.url}}';
                  // Same UX hint as multimedia recommender mode: the placeholder signals that card
                  // images come from the product feed, and the backend's
                  // resolveRecommenderSampleImage substitutes one real feed image as the
                  // Meta-approval sample at save time.
                  if (!whatsappMessage.imageUrl) change.imageUrl = '{{product.imageUrl}}';
                  if (!whatsappMessage.cardBody) change.cardBody = RECOMMENDER_CARD_BODY_PREFILL;
                  if (!whatsappMessage.numCards) change.numCards = 3;
                  // Product tags stay out of the top-level body - with N cards the reference
                  // would be ambiguous, so the prefill only personalizes by profile fields.
                  if (!whatsappMessage.body) change.body = RECOMMENDER_CAROUSEL_BODY_PREFILL;
                  change.cards = [];
                } else {
                  change.productFeedId = null;
                  // Card count comes from the cards array in static mode.
                  change.numCards = null;
                  // Button/Image URL are recommender-only fields here, so clear them outright
                  // rather than only when they still hold the exact pre-fill - a user-edited
                  // value would otherwise be submitted from a field the form no longer shows.
                  change.btnUrl = '';
                  change.imageUrl = '';
                  // Card body and message stay visible and editable in static mode, so only drop
                  // them when they're still the untouched recommender pre-fill.
                  if (whatsappMessage.cardBody === RECOMMENDER_CARD_BODY_PREFILL) change.cardBody = '';
                  if (whatsappMessage.body === RECOMMENDER_CAROUSEL_BODY_PREFILL) change.body = '';
                }
                setWhatsappMessage({ ...whatsappMessage, ...change });
              }}
              data={[
                { label: 'Static', value: 'static' },
                { label: 'Recommender', value: 'recommender' },
              ]}
            />
            <Text size="xs" c="dimmed" mt="xs">
              {isRecommender
                ? 'Recommender mode fills each card with one recommended product per recipient from the selected feed. Recipients with fewer recommended products than cards are skipped. WhatsApp center-crops card images to a wide ratio (about 1.91:1), so tall product-feed images lose their top and bottom edges.'
                : 'Static mode sends the same 2-10 cards to every recipient. Each card has its own image and button URL. WhatsApp center-crops card images to a wide ratio (about 1.91:1) - use landscape images with the same ratio on every card.'}
            </Text>
          </Grid.Col>
        </Grid>

        {isRecommender && (
          <>
            <Grid mb="sm">
              <Grid.Col span={2}>
                <Text size="sm" fw={500}>
                  Product Feed*
                </Text>
              </Grid.Col>
              <Grid.Col span={10}>
                <ProductFeedSelector
                  accountId={domain.accountId}
                  domainId={domain.id}
                  productFeedId={whatsappMessage.productFeedId}
                  onSelect={(productFeedId) => setWhatsappMessage({ ...whatsappMessage, productFeedId })}
                  scope="marketingChannels"
                  forType="whatsapp message"
                />
              </Grid.Col>
            </Grid>

            <Grid mb="sm">
              <Grid.Col span={2}>
                <Text size="sm" fw={500}>
                  Number of Cards*
                </Text>
              </Grid.Col>
              <Grid.Col span={10}>
                <NumberInput
                  w={120}
                  aria-label="Number of Cards"
                  min={2}
                  max={10}
                  step={1}
                  // A fractional card count is meaningless to Meta and would fail the backend's
                  // Number.isInteger check.
                  allowDecimal={false}
                  allowNegative={false}
                  value={whatsappMessage.numCards ?? ''}
                  error={carouselNumCardsError}
                  onChange={(value) =>
                    // Mantine emits the raw STRING for partial entries ('', '03'); normalize so an
                    // in-range value is never submitted as a string.
                    handleChange('numCards', normalizeCardCount(value))
                  }
                  disabled={cardCountLocked}
                />
                <Text size="xs" c="dimmed" mt="xs">
                  Between 2 and 10. Meta fixes the card count when the template is created - it cannot be changed
                  afterwards.
                </Text>
              </Grid.Col>
            </Grid>
          </>
        )}

        {!isRecommender && (
          <Grid mb="sm">
            <Grid.Col span={2}>
              <Text size="sm" fw={500}>
                Cards ({cards.length})
              </Text>
            </Grid.Col>
            <Grid.Col span={10}>
              <WhatsappCarouselCardList
                cards={cards}
                onEdit={(index) => setEditingCardIndex(index)}
                onDelete={deleteCard}
                onReorder={(reordered) => setWhatsappMessage({ ...whatsappMessage, cards: reordered })}
                countLocked={cardCountLocked}
              />
              {!cardCountLocked && (
                <Flex align="center" gap="sm" mt="xs">
                  <Button
                    variant="default"
                    onClick={() => setEditingCardIndex(cards.length)}
                    disabled={cards.length >= 10}
                  >
                    Add Card
                  </Button>
                  <Text size="xs" c="dimmed">
                    Between 2 and 10 cards. The card count is fixed once the template is created.
                  </Text>
                </Flex>
              )}
            </Grid.Col>
          </Grid>
        )}

        <Grid mb="sm">
          <Grid.Col span={2}>
            <Text size="sm" fw={500}>
              Button Text*
            </Text>
          </Grid.Col>
          <Grid.Col span={10}>
            <TextInput
              name="btnText"
              value={whatsappMessage.btnText || ''}
              placeholder="e.g. Shop Now"
              error={
                !whatsappMessage.btnText
                  ? 'Button Text is required for carousel cards.'
                  : whatsappMessage.btnText.length > MAX_BUTTON_TEXT_LENGTH
                    ? `Button text must be at most ${MAX_BUTTON_TEXT_LENGTH} characters - Meta rejects longer labels.`
                    : null
              }
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              disabled={noEditing}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Shown on every card&apos;s button - Meta requires all cards to share the same structure.
            </Text>
          </Grid.Col>
        </Grid>

        {isRecommender && (
          <>
            <Grid mb="sm">
              <Grid.Col span={2}>
                <Text size="sm" fw={500}>
                  Button URL*
                </Text>
              </Grid.Col>
              <Grid.Col span={10}>
                <TextInput
                  name="btnUrl"
                  value={whatsappMessage.btnUrl || ''}
                  placeholder="{{product.url}}"
                  error={
                    btnUrlHandlebarsError ||
                    (whatsappMessage.btnUrl && !isValidUrl
                      ? 'Invalid URL. Please check the address for typing errors or missing protocol.'
                      : !whatsappMessage.btnUrl
                        ? 'Button URL is required for carousel cards.'
                        : null)
                  }
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  disabled={noEditing}
                />
                <Text size="xs" c="dimmed" mt="xs">
                  Resolved per card against that card&apos;s product - keep {'{{product.url}}'} to link each card to its
                  own product.
                </Text>
              </Grid.Col>
            </Grid>

            <Grid mb="sm">
              <Grid.Col span={2}>
                <Text size="sm" fw={500}>
                  Image URL*
                </Text>
              </Grid.Col>
              <Grid.Col span={10}>
                <Flex gap="xs">
                  <TextInput
                    style={{ flex: 1 }}
                    name="imageUrl"
                    value={whatsappMessage.imageUrl || ''}
                    placeholder="{{product.imageUrl}}"
                    error={carouselImageUrlError}
                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                    disabled={noEditing}
                  />
                  <Button onClick={() => setImagePickerOpen(true)} disabled={noEditing}>
                    Browse
                  </Button>
                </Flex>
                <AssetPickerModal
                  key="whatsapp-carousel-sample-asset"
                  show={imagePickerOpen}
                  close={() => setImagePickerOpen(false)}
                  confirm={(asset, _, cb) => {
                    setWhatsappMessage({ ...whatsappMessage, imageUrl: asset.url });
                    cb();
                  }}
                />
                <Text size="xs" c="dimmed" mt="xs">
                  Only used as the sample for Meta template approval - actual sends use each card&apos;s product image.
                  By default the platform picks a real product image from the selected feed automatically; click Browse
                  only if you want to override that sample.
                </Text>
              </Grid.Col>
            </Grid>
          </>
        )}

        <Grid mb="sm">
          <Grid.Col span={2}>
            <Text size="sm" fw={500}>
              Card Body
            </Text>
          </Grid.Col>
          <Grid.Col span={10}>
            <Flex gap="xs" mb="xs">
              <Button
                size="sm"
                variant="default"
                onClick={() => setCardBodyTagsOpen(!cardBodyTagsOpen)}
                aria-expanded={cardBodyTagsOpen}
              >
                <IconInfoCircle size={14} /> Merge Tags
              </Button>
            </Flex>
            <MergeTagsCollapsible
              open={cardBodyTagsOpen}
              // The card body is where {{product.*}} tags belong for recommender carousels -
              // each card resolves them against its own product.
              mergeTags={getMergeTags(customFields, coupons, surveys, { includeProduct: isRecommender })}
            />
            <Textarea
              autosize
              minRows={2}
              name="cardBody"
              aria-label="Card Body"
              value={whatsappMessage.cardBody || ''}
              placeholder={isRecommender ? RECOMMENDER_CARD_BODY_PREFILL : 'Optional text shown under every card image'}
              error={
                cardBodyHandlebarsError || cardBodyEdgeMergeTagError || cardBodyProductTagError || cardBodyLengthError
              }
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              disabled={noEditing}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Optional. Length: {whatsappMessage.cardBody?.length || 0} / {maxCardBodyLength}. Applied to every card
              {isRecommender ? " - {{product.*}} tags resolve against each card's own product." : '.'} Meta requires
              enough plain text around merge tags - a short card body with several tags is rejected at save time with
              &quot;Params Words Ratio Exceeds Limit&quot;, so wrap each tag in a full sentence.
            </Text>
          </Grid.Col>
        </Grid>
      </>
    );
  }

  let deleteButton;
  if (deleteWhatsappMessage) {
    deleteButton = (
      <LockableButton restrictionKey="whatsappMessagesDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  const duplicateButton = whatsappMessage.id && (
    <LockableButton
      restrictionKey="whatsappMessagesCreate"
      color="cyan"
      onClick={() => {
        navigate(
          location.pathname.replace(
            `/whatsapp/${whatsappMessage.id}/details`,
            `/whatsapp/new?from=${whatsappMessage.id}`
          )
        );
      }}
    >
      <IconCopy size={14} /> Make a copy
    </LockableButton>
  );

  const testMessageButton = (
    <Button
      color="cyan"
      size="sm"
      disabled={!isValid || !whatsappMessage.id}
      onClick={() => {
        setTestWhatsappMessageOpen(true);
      }}
    >
      <IconBrandWhatsapp size={14} /> Test Message
    </Button>
  );

  return (
    <>
      <SendTestMessageModal
        send={send}
        close={() => setTestWhatsappMessageOpen(false)}
        error={error}
        sending={sending}
        show={testWhatsappMessageOpen}
        sendToPlaceholder="Paste phone numbers (with +<countryCode>) separated by ','"
        sendTo={user.phoneNumber}
        isSendToValid={(sendTo) => sendTo.split(',').every((v) => Regex.phoneNumberRegex.test(v))}
      />
      <WhatsappCarouselCardEditor
        card={editingCardIndex !== null ? cards[editingCardIndex] || EMPTY_CARD : null}
        opened={editingCardIndex !== null}
        disabled={noEditing}
        onSave={saveCard}
        onClose={() => setEditingCardIndex(null)}
      />
      <form onSubmit={save}>
        <Grid mb="sm">
          <Grid.Col span={2}>
            <Text size="sm" fw={500}>
              Name*
            </Text>
          </Grid.Col>
          <Grid.Col span={10}>
            <TextInput
              disabled={whatsappMessage.id}
              name="name"
              value={whatsappMessage.name}
              error={
                isInvalidName(whatsappMessage.name)
                  ? 'Please use only lowercase letters, numbers, or underscores ( _ )'
                  : null
              }
              placeholder="test_whatsapp_message"
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Please note that once saved, the name of a template message cannot be edited.
            </Text>
          </Grid.Col>
        </Grid>

        <Grid mb="sm">
          <Grid.Col span={2}>
            <Text size="sm" fw={500}>
              Type
            </Text>
          </Grid.Col>
          <Grid.Col span={10}>
            <SegmentedControl
              disabled={!!whatsappMessage.id}
              value={whatsappMessage.type ?? ''}
              onChange={(type) => {
                const change = { type };
                if (type === 'multimedia') {
                  // Persist the default explicitly so the backend doesn't have to guess on a fresh
                  // template that the user never touched the mode selector for.
                  if (!whatsappMessage.mode) change.mode = 'static';
                  // Carousel-only fields are dead data on multimedia templates.
                  change.cards = [];
                  change.numCards = null;
                  change.cardBody = '';
                } else if (type === 'carousel') {
                  const mode = whatsappMessage.mode || 'static';
                  if (!whatsappMessage.mode) change.mode = 'static';
                  change.btnType = 'URL';
                  // Meta carousel templates support only a message body plus the cards - no
                  // header or footer components.
                  change.header = '';
                  change.footer = '';
                  if (mode === 'recommender') {
                    // numCards only drives recommender carousels; static ones take the count from
                    // the cards array.
                    if (!whatsappMessage.numCards) change.numCards = 3;
                  } else {
                    change.numCards = null;
                    // The top-level Button/Image URL fields aren't rendered for static carousels
                    // (each card carries its own), so anything left there - notably the
                    // {{product.*}} pre-fills from multimedia recommender mode - is invisible,
                    // unvalidated state that must not be submitted.
                    change.btnUrl = '';
                    change.imageUrl = '';
                  }
                } else {
                  // Text-based templates don't have any media/button/feed fields — clear them so we
                  // don't silently submit leftover state (or orphan {{product.*}} placeholders).
                  // `mode` is dead data for text templates; null it out rather than carrying a
                  // misleading 'static' value.
                  change.mode = null;
                  change.productFeedId = null;
                  change.btnText = '';
                  change.btnUrl = '';
                  change.imageUrl = '';
                  change.cards = [];
                  change.numCards = null;
                  change.cardBody = '';
                }
                setWhatsappMessage({ ...whatsappMessage, ...change });
              }}
              data={[
                { label: 'Text-Based', value: 'text' },
                { label: 'Media-Based', value: 'multimedia' },
                { label: 'Carousel', value: 'carousel' },
              ]}
            />
          </Grid.Col>
        </Grid>

        {mediaConfig}

        {carouselConfig}

        {whatsappMessage.type === 'text' && (
          <Grid mb="sm">
            <Grid.Col span={2}>
              <Text size="sm" fw={500}>
                Header
              </Text>
            </Grid.Col>
            <Grid.Col span={10}>
              <TextInput
                name="header"
                value={whatsappMessage.header}
                placeholder="Header Message"
                error={
                  headerHandlebarsError || (!isValidField.header ? 'Header text must be less than 60 symbols.' : null)
                }
                onChange={(e) => handleFieldChange(e.target.name, e.target.value)}
                disabled={noEditing}
              />
            </Grid.Col>
          </Grid>
        )}

        <Grid mb="sm">
          <Grid.Col span={2}>
            <Text size="sm" fw={500}>
              Message*
            </Text>
          </Grid.Col>
          <Grid.Col span={10}>
            <Flex gap="xs" mb="xs">
              <Button
                size="sm"
                variant="default"
                onClick={() => setDynamicFieldsOpen(!dynamicFieldsOpen)}
                aria-expanded={dynamicFieldsOpen}
              >
                <IconInfoCircle size={14} /> Merge Tags
              </Button>
              {testMessageButton}
            </Flex>
            <MergeTagsCollapsible
              open={dynamicFieldsOpen}
              // Carousel message bodies reject {{product.*}} tags (which card would they refer
              // to?), so don't offer them here - the Card Body field has its own tag reference.
              mergeTags={getMergeTags(customFields, coupons, surveys, {
                includeProduct: isRecommender && !isCarousel,
              })}
            />

            <Textarea
              rows={7}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              name="body"
              aria-label="Message"
              value={whatsappMessage.body}
              error={bodyHandlebarsError || bodyEdgeMergeTagError || bodyProductTagError}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Length: {whatsappMessage.body?.length || 0} / {maxMessageLength}. Messages can contain up to{' '}
              {maxMessageLength} characters.
            </Text>
          </Grid.Col>
        </Grid>

        {/* Meta carousel templates have no FOOTER component - hide the field so a typed footer
            can't reach the model's rejection. */}
        {!isCarousel && (
          <Grid mb="sm">
            <Grid.Col span={2}>
              <Text size="sm" fw={500}>
                Footer
              </Text>
            </Grid.Col>
            <Grid.Col span={10}>
              <TextInput
                name="footer"
                value={whatsappMessage.footer}
                error={
                  !isValidField.footer || hasTags(whatsappMessage.footer)
                    ? 'Footer text must be less than 60 symbols and Merge tags are not allowed.'
                    : null
                }
                placeholder="Footer Message"
                onChange={(e) => handleFieldChange(e.target.name, e.target.value)}
                disabled={noEditing}
              />
            </Grid.Col>
          </Grid>
        )}

        <DailyLimitSettings
          model={whatsappMessage}
          onChange={(key, value) => setWhatsappMessage({ ...whatsappMessage, [key]: value })}
          channelLabel="WhatsApp messages"
          maxPerDay={domain.maxWhatsappMessagesPerDay}
          accountId={domain.accountId}
          domainId={domain.id}
          settingsTab="whatsapp"
          disableSendAnyway
        />
        <Group justify="flex-end" gap="xs" py="md">
          <Button variant="default" onClick={onBack}>
            Back
          </Button>
          {deleteButton}
          {duplicateButton}
          {showUserFlow && (
            <Button variant="default" onClick={() => showUserFlow()}>
              <IconMail size={14} /> Send
            </Button>
          )}
          <LockableButton
            restrictionKey={`whatsappMessages${whatsappMessage.id ? 'Update' : 'Create'}`}
            type="submit"
            disabled={
              !isValid ||
              isInvalidName(whatsappMessage.name) ||
              // The Footer field isn't rendered for carousels and buildPayload drops it, so a
              // stale tagged footer must not disable Save with no visible field to fix.
              (!isCarousel && hasTags(whatsappMessage.footer)) ||
              saving ||
              noEditing
            }
          >
            <IconDeviceFloppy size={14} /> {saving ? 'Saving...' : 'Save'}
          </LockableButton>
        </Group>
        <ConfirmationModal
          show={confirmDeleteOpen}
          close={() => setConfirmDeleteOpen(false)}
          confirm={(cb) => deleteWhatsappMessage(cb)}
          message="This action cannot be undone!"
        />
      </form>
    </>
  );
};

export default WhatsappMessageForm;
